import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API_URL from "../apiConfig";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getLanguage() {
  if (typeof window === "undefined") return "es";

  if (window.location.pathname.startsWith("/fr/")) return "fr";
  return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) === "fr" ? "fr" : "es";
}

const COPY = {
  es: {
    eyebrow: "Edición Fundadora",
    title: "Mente en Dirección",
    intro: "Mente en Dirección es una experiencia práctica de Dirección Personal para convertir claridad en decisiones, decisiones en acciones y acciones en evidencia de avance sobre un objetivo real.",
    note: "La Edición Fundadora aún no está abierta a compra. Si quieres conocer la fecha de apertura y recibir la información de esta primera edición, registra tu interés aquí.",
    name: "Nombre",
    email: "Correo electrónico",
    consent: "Quiero recibir por correo la información relacionada con la Edición Fundadora de Mente en Dirección. Entiendo que registrarme no constituye una compra ni una reserva pagada.",
    submit: "Registrar mi interés",
    sending: "Registrando...",
    successTitle: "Tu interés quedó registrado.",
    successWithEmail: "Te enviaremos la información de apertura de la Edición Fundadora al correo indicado. También enviamos una confirmación a ese correo.",
    successWithoutEmail: "Tu registro quedó guardado correctamente. Te enviaremos la información de apertura de la Edición Fundadora al correo indicado.",
    error: "No pudimos registrar tu interés en este momento. Inténtalo nuevamente.",
    back: "Volver a ZEDRO Academy",
    privacy: "Consulta nuestra Política de Privacidad.",
  },
  fr: {
    eyebrow: "Édition fondatrice",
    title: "Clarté en action",
    intro: "Clarté en action est une expérience pratique de Direction personnelle conçue pour transformer la clarté en décisions, les décisions en actions et les actions en preuves de progrès sur un objectif réel.",
    note: "L’Édition fondatrice n’est pas encore ouverte à l’achat. Si vous souhaitez connaître la date d’ouverture et recevoir les informations sur cette première édition, enregistrez votre intérêt ici.",
    name: "Nom",
    email: "Courriel",
    consent: "Je souhaite recevoir par courriel les informations liées à l’Édition fondatrice de Clarté en action. Je comprends que cette inscription ne constitue ni un achat ni une réservation payante.",
    submit: "Enregistrer mon intérêt",
    sending: "Enregistrement...",
    successTitle: "Votre intérêt a bien été enregistré.",
    successWithEmail: "Nous vous enverrons les informations d’ouverture de l’Édition fondatrice à l’adresse indiquée. Nous avons également envoyé une confirmation à cette adresse.",
    successWithoutEmail: "Votre inscription a bien été enregistrée. Nous vous enverrons les informations d’ouverture de l’Édition fondatrice à l’adresse indiquée.",
    error: "Nous n’avons pas pu enregistrer votre intérêt pour le moment. Veuillez réessayer.",
    back: "Retour à ZEDRO Academy",
    privacy: "Consultez notre Politique de confidentialité.",
  },
};

export default function FounderEditionInterest() {
  const [language, setLanguage] = useState(getLanguage);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [confirmationSent, setConfirmationSent] = useState(false);
  const copy = COPY[language] || COPY.es;

  useEffect(() => {
    document.documentElement.lang = language === "fr" ? "fr-CA" : "es";
  }, [language]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    function syncLanguage(event) {
      const nextLanguage = event?.detail?.language;
      if (nextLanguage === "es" || nextLanguage === "fr") {
        setLanguage(nextLanguage);
      }
    }

    window.addEventListener("zedro:languagechange", syncLanguage);
    return () => window.removeEventListener("zedro:languagechange", syncLanguage);
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!consent || !email.trim()) return;
    setStatus("sending");

    try {
      const response = await fetch(`${API_URL}/api/founder-edition-interests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim().toLowerCase(),
          language,
          source: "founder-edition-page",
          productSlug: "mente-en-direccion",
          consent: true,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.ok !== true) throw new Error(data?.error || "registration_failed");
      setConfirmationSent(data?.confirmationSent === true);
      setStatus("success");
    } catch (error) {
      console.error("[FOUNDER_EDITION_INTEREST_FORM]", error);
      setStatus("error");
    }
  }

  return (
    <main style={styles.page}>
      <section style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.back}>{copy.back}</Link>
        </div>

        <div style={styles.card}>
          <div style={styles.copyColumn}>
            <div style={styles.eyebrow}>{copy.eyebrow}</div>
            <h1 style={styles.title}>{copy.title}</h1>
            <p style={styles.intro}>{copy.intro}</p>
            <p style={styles.note}>{copy.note}</p>
          </div>

          <div style={styles.formColumn}>
            {status === "success" ? (
              <div role="status" style={styles.successBox}>
                <h2 style={styles.successTitle}>{copy.successTitle}</h2>
                <p style={styles.body}>{confirmationSent ? copy.successWithEmail : copy.successWithoutEmail}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={styles.form}>
                <label style={styles.label}>
                  {copy.name}
                  <input maxLength={120} value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" style={styles.input} />
                </label>

                <label style={styles.label}>
                  {copy.email}
                  <input type="email" maxLength={254} required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" style={styles.input} />
                </label>

                <label style={styles.consentRow}>
                  <input type="checkbox" required checked={consent} onChange={(event) => setConsent(event.target.checked)} />
                  <span>{copy.consent}</span>
                </label>

                {status === "error" ? <div role="alert" style={styles.error}>{copy.error}</div> : null}

                <button type="submit" disabled={!consent || status === "sending"} style={!consent || status === "sending" ? styles.buttonDisabled : styles.button}>
                  {status === "sending" ? copy.sending : copy.submit}
                </button>

                <Link to="/legal/privacidad" style={styles.privacy}>{copy.privacy}</Link>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#f6f8fa", padding: "1.5rem 1.25rem 3rem", color: "#29323C" },
  shell: { maxWidth: 1040, margin: "0 auto" },
  topRow: { display: "flex", alignItems: "center", marginBottom: 16 },
  back: { color: "#264763", textDecoration: "none", fontWeight: 700, fontSize: 15 },
  card: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 24, background: "#fff", border: "1px solid #e3e9ee", borderRadius: 20, padding: "clamp(22px,4vw,42px)", boxShadow: "0 16px 42px rgba(38,71,99,.07)" },
  copyColumn: { alignSelf: "center" },
  eyebrow: { color: "#CCA352", textTransform: "uppercase", letterSpacing: ".14em", fontWeight: 800, fontSize: 11 },
  title: { margin: "8px 0 16px", color: "#264763", fontFamily: "Bitter, serif", fontSize: "clamp(2.35rem,4.8vw,4rem)", lineHeight: 1.02 },
  intro: { margin: 0, color: "#425d72", lineHeight: 1.65, fontSize: 16 },
  note: { margin: "16px 0 0", color: "#607486", lineHeight: 1.65, fontSize: 15 },
  formColumn: { background: "#f8fafb", border: "1px solid #e5ebef", borderRadius: 16, padding: 20 },
  form: { display: "grid", gap: 14 },
  label: { display: "grid", gap: 6, color: "#264763", fontWeight: 700, fontSize: 15 },
  input: { width: "100%", boxSizing: "border-box", border: "1px solid #cfd9e1", borderRadius: 10, padding: "11px 12px", font: "inherit", background: "#fff" },
  consentRow: { display: "grid", gridTemplateColumns: "18px 1fr", gap: 9, alignItems: "start", color: "#526b7f", fontSize: 13, lineHeight: 1.5 },
  button: { border: 0, borderRadius: 999, background: "#264763", color: "#fff", padding: "12px 17px", fontWeight: 800, cursor: "pointer", fontSize: 15 },
  buttonDisabled: { border: 0, borderRadius: 999, background: "#aab8c3", color: "#fff", padding: "12px 17px", fontWeight: 800, cursor: "not-allowed", fontSize: 15 },
  privacy: { textAlign: "center", color: "#607486", fontSize: 12.5 },
  error: { color: "#9b2c2c", background: "#fff5f5", border: "1px solid #fed7d7", borderRadius: 10, padding: 10, fontSize: 14 },
  successBox: { background: "#fff", border: "1px solid #dbe6df", borderRadius: 14, padding: 20 },
  successTitle: { color: "#264763", margin: "0 0 10px", fontFamily: "Bitter, serif" },
  body: { color: "#526b7f", lineHeight: 1.65, margin: 0 },
};
